import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import ApiRequest from "../../../actions/ApiRequest";

const LOG_FILES = [
  { value: "detailed", label: "Detailed" },
  { value: "errors", label: "Errors" },
  { value: "performance", label: "Performance" },
];

const REFRESH_MS = 3000;

const LiveLog = ({ isDarktheme }) => {
  const [lines, setLines] = useState([]);
  const [file, setFile] = useState("detailed");
  const [count, setCount] = useState(200);
  const [filter, setFilter] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const preRef = useRef(null);
  const intervalRef = useRef(null);
  // keep latest values available to the interval without re-creating it
  const paramsRef = useRef({ file, count, filter });
  paramsRef.current = { file, count, filter };

  const fetchLog = useCallback(async () => {
    const { file: f, count: c, filter: flt } = paramsRef.current;
    setLoading(true);
    setError("");
    try {
      const params = { file: f, lines: c };
      if (flt.trim()) params.filter = flt.trim();
      const { data } = await ApiRequest.post("get_live_log", null, { params });
      if (data?.status === "success") {
        setLines(data.lines || []);
        setLastUpdated(new Date());
      } else {
        setError(data?.reason || "Failed to load log");
      }
    } catch (e) {
      setError(e?.message || "Failed to load log");
    } finally {
      setLoading(false);
    }
  }, []);

  // initial load + whenever file/count/filter change
  useEffect(() => {
    fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, count, filter]);

  // auto-refresh interval
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLog, REFRESH_MS);
      return () => clearInterval(intervalRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, fetchLog]);

  // keep scrolled to bottom when new lines arrive
  useEffect(() => {
    if (preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [lines]);

  const lineColor = (ln) => {
    if (/\|\s*ERROR\s*\|/.test(ln)) return "#ff6b6b";
    if (/\|\s*WARNING\s*\|/.test(ln)) return "#ffd166";
    if (/\|\s*DEBUG\s*\|/.test(ln)) return "#9aa5b1";
    return isDarktheme ? "#d1f7c4" : "#1b5e20";
  };

  const labelStyle = { fontSize: "0.85rem", marginRight: 6, fontWeight: 600 };
  const controlStyle = { padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" };

  return (
    <div style={{ padding: 8 }}>
      <h2 style={{ marginBottom: 16 }}>📜 Live Server Log</h2>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <span style={labelStyle}>File:</span>
          <select
            style={controlStyle}
            value={file}
            onChange={(e) => setFile(e.target.value)}
          >
            {LOG_FILES.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span style={labelStyle}>Lines:</span>
          <select
            style={controlStyle}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          >
            {[100, 200, 500, 1000, 2000].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span style={labelStyle}>Filter:</span>
          <input
            style={{ ...controlStyle, width: 220 }}
            type="text"
            placeholder="e.g. verification"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span style={labelStyle}>Auto-refresh (3s)</span>
        </label>

        <button
          className="btn btn-primary"
          onClick={fetchLog}
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
                style={{ marginRight: 6 }}
              />
              Loading…
            </>
          ) : (
            "Refresh"
          )}
        </button>

        <span style={{ fontSize: "0.8rem", color: "#888" }}>
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ""}
          {lines.length ? ` · ${lines.length} lines` : ""}
        </span>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 8 }}>
          {error}
        </div>
      )}

      <pre
        ref={preRef}
        style={{
          background: isDarktheme ? "#0d1117" : "#1e1e1e",
          color: "#d1f7c4",
          padding: 16,
          borderRadius: 8,
          height: "65vh",
          overflow: "auto",
          fontFamily: "Consolas, 'Courier New', monospace",
          fontSize: "0.8rem",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          direction: "ltr",
          textAlign: "left",
        }}
      >
        {lines.length === 0
          ? "— no log lines —"
          : lines.map((ln, i) => (
              <div key={i} style={{ color: lineColor(ln) }}>
                {ln}
              </div>
            ))}
      </pre>
    </div>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps, null)(LiveLog);
